import React from 'react';
import PropTypes from 'prop-types';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { MemberProfilePanel } from '@adsk/acs-admin-ui-components-shared-components';
import ProjectMemberOptionsMenu from '@adsk/acs-admin-ui-components-project-member-options-menu';
import ProductsSection from './layout/ProductsSection';
import AccessLevelField from './layout/AccessLevelField';
import RemoveRoleAndMemberConfirmationModal from './layout/RemoveRoleAndMemberConfirmationModal';
import RemoveRoleAndCurrentProductAccessConfirmationModal from './layout/RemoveRoleAndCurrentProductAccessConfirmationModal';
import { userPropType } from './propTypes';
import { progressRingStyle } from './styles';

const ProjectMemberProfilePanel = ({
  companies,
  companySearchInput,
  isEmsAccount,
  isFetchingCompanies,
  isFetchingRoles,
  isLoadingProductsSection,
  onCancelPanelClose,
  onChangeCompanyInput,
  onConfirmPanelClose,
  onCompanyChange,
  onResend,
  onRemove,
  onRoleChange,
  onSaveData,
  products,
  roles,
  saveMemberData,
  selectedCompany,
  selectedRoles,
  showClosePanelConfirmation,
  showProductsSection,
  showProductsSectionRedesign,
  user,
  useWriteConstructionApis,
  ...rest
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const {
    values: {
      showRemoveRoleAndCurrentProductAccessConfirmation,
      showRemoveRoleConfirmation,
    },
    setFieldValue,
  } = rest;

  const handleRemoveRoleModalClose = () => {
    setFieldValue('showRemoveRoleConfirmation', false);
  };
  const handleRemoveRoleModalSuccess = () => {
    setFieldValue('showRemoveRoleConfirmation', false);
    onRemove(true);
  };
  const handleRemoveRoleAndCurrentProductAccessModalClose = () => {
    setFieldValue('showRemoveRoleAndCurrentProductAccessConfirmation', null);
  };
  const handleRemoveRoleAndCurrentProductAccessModalConfirm = () => {
    onRoleChange(showRemoveRoleAndCurrentProductAccessConfirmation, null, true);
    setFieldValue('showRemoveRoleAndCurrentProductAccessConfirmation', null);
  };

  return (
    <MemberProfilePanel
      {...rest}
      companies={companies}
      companySearchInput={companySearchInput}
      isEmsAccount={isEmsAccount}
      isFetchingCompanies={isFetchingCompanies}
      isFetchingRoles={isFetchingRoles}
      onChangeCompanyInput={onChangeCompanyInput}
      onCompanyChange={onCompanyChange}
      onRoleChange={onRoleChange}
      optionsMenu={
        user && (
          <ProjectMemberOptionsMenu
            email={user.email}
            horizontal
            imageUrl={user.imageUrl}
            name={user.name}
            onRemove={onRemove}
            onResend={onResend}
            status={user.status}
            style={{ border: 'none' }}
            userId={user.id}
            useWriteConstructionApis={useWriteConstructionApis}
          />
        )
      }
      roles={roles}
      selectedCompany={selectedCompany}
      selectedRole={selectedRoles}
      user={user}
    >
      <AccessLevelField
        isEmsAccount={isEmsAccount}
        member={user}
        onRemove={onRemove}
        saveMemberData={saveMemberData}
      />
      {isLoadingProductsSection && (
        <div style={progressRingStyle}>
          <ProgressRing size="medium" />
        </div>
      )}
      {!isLoadingProductsSection && showProductsSection && !isEmsAccount && (
        // note - disabling this section for fiano release by adding !isEmsAccount condition
        // post fiano ems accounts will have assignable products
        <ProductsSection
          isEmsAccount={isEmsAccount}
          member={user}
          onRemove={onRemove}
          onSaveData={onSaveData}
          permissionsEditState={false}
          products={products}
          showProductsSectionRedesign={showProductsSectionRedesign}
        />
      )}
      <ModalConfirmation
        cancelButtonLabel={T.translate('CANCEL_BUTTON')}
        confirmationButtonLabel={T.translate('LEAVE_ANYWAY')}
        data-testid="ClosePanel_ModalConfirmation"
        disableModalExit
        isConfirmationButtonAlert
        message={T.translate('CLOSE_PANEL_CONFIRMATION_BODY')}
        onClose={onCancelPanelClose}
        onConfirm={onConfirmPanelClose}
        show={showClosePanelConfirmation}
        title={T.translate('CLOSE_PANEL_CONFIRMATION_TITLE')}
      />
      <RemoveRoleAndMemberConfirmationModal
        member={user}
        onClose={handleRemoveRoleModalClose}
        onRemove={handleRemoveRoleModalSuccess}
        show={showRemoveRoleConfirmation}
        useConstructionApis={useWriteConstructionApis}
      />
      <RemoveRoleAndCurrentProductAccessConfirmationModal
        onClose={handleRemoveRoleAndCurrentProductAccessModalClose}
        onConfirm={handleRemoveRoleAndCurrentProductAccessModalConfirm}
        show={!!showRemoveRoleAndCurrentProductAccessConfirmation}
      />
    </MemberProfilePanel>
  );
};

ProjectMemberProfilePanel.displayName = 'ProjectMemberProfilePanel';

ProjectMemberProfilePanel.propTypes = {
  companies: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  companySearchInput: PropTypes.string.isRequired,
  isEmsAccount: PropTypes.bool,
  isFetchingCompanies: PropTypes.bool.isRequired,
  isFetchingRoles: PropTypes.bool.isRequired,
  isLoadingProductsSection: PropTypes.bool.isRequired,
  onCancelPanelClose: PropTypes.func.isRequired,
  onChangeCompanyInput: PropTypes.func.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  onConfirmPanelClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  onSaveData: PropTypes.func,
  products: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  roles: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  saveMemberData: PropTypes.func.isRequired,
  selectedCompany: PropTypes.objectOf(PropTypes.string),
  selectedRoles: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
    .isRequired,
  showClosePanelConfirmation: PropTypes.bool.isRequired,
  showProductsSection: PropTypes.bool.isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
  user: userPropType.isRequired,
  useWriteConstructionApis: PropTypes.bool.isRequired,
};

ProjectMemberProfilePanel.defaultProps = {
  isEmsAccount: false,
  onSaveData: undefined,
  selectedCompany: null,
};

export default ProjectMemberProfilePanel;
